import * as React from 'react';

import { routes } from 'defs';
import i18n from 'helper/i18n';

import { GenderKeyType } from 'types/GenderKey';

import Checkbox from 'components/atoms/Checkbox';
import Radio from 'components/atoms/Radio';
import Spacer from 'components/helper/Spacer';
import Agreement, { uiStateClasses as agreementUiStateClasses } from 'components/molecules/Agreement';
import ButtonBar from 'components/molecules/ButtonBar';
import { ButtonBarProps } from 'components/molecules/ButtonBar';
import ElementList from 'components/molecules/ElementList';
import GenderSelector from 'components/molecules/GenderSelector';
import { GenderProps } from 'components/molecules/GenderSelector';
import { AnamnesisStepResolver } from 'components/organisms/Anamnesis';

export type SelectGenderProps = {
	gender?: GenderKeyType;
	genders?: GenderProps[];
	genderMeta?: Object;
	isPregnant: boolean | null;
	acceptedWarnHint: boolean | null;
	actionButtonsActive: ButtonBarProps;
	actionButtonsInactive: ButtonBarProps;
	isUpdatingAnamnesis: boolean;
	onNavigationClick: (targetUrl: { href: string }) => void /* parent */;
	onStepCompleted: (resolver: AnamnesisStepResolver) => void /* parent */;
	doSaveGender?: Function /* actionCreator */;
};

export type SelectGenderState = {
	gender: GenderKeyType;
	selectedGender: GenderKeyType;
	acceptedWarnHint: void | boolean;
	isPregnant: void | boolean;
};

export default class SelectGender extends React.Component<SelectGenderProps, SelectGenderState> {
	public static displayName = 'AnamnesisSelectGender';
	public static defaultProps = {
		genders: [],
		isPregnant: null,
		actionButtonsActive: {},
		actionButtonsInactive: {},
	};

	constructor(props: SelectGenderProps) {
		super(props);

		this.state = {
			gender: props.gender || 'F',
			selectedGender: props.gender || 'F',
			isPregnant: props.isPregnant,
			acceptedWarnHint: props.acceptedWarnHint,
		} as SelectGenderState;
	}

	public componentDidUpdate(prevProps: SelectGenderProps) {
		if (this.props.gender !== prevProps.gender || this.props.isPregnant !== prevProps.isPregnant) {
			this.setState({
				selectedGender: this.props.gender as GenderKeyType,
				acceptedWarnHint: this.props.isPregnant !== null ? true : this.state.acceptedWarnHint,
				isPregnant: this.props.isPregnant !== null ? this.props.isPregnant : this.state.isPregnant,
			});
		}
	}

	public onHandleButtonClicks = (type: string) => {
		const _this = this;
		const { onNavigationClick, onStepCompleted } = this.props;

		if (type === 'completed' && typeof onStepCompleted === 'function') {
			return onStepCompleted.apply(null, [
				{
					actionCreator: _this.props.doSaveGender,
					waitFor: 'savedGender',
					nextRoute: routes.anamnesis.determineAge,
					data: {
						gender: this.state.selectedGender,
						isPregnant: this.state.isPregnant,
						tempPregnant: this.state.selectedGender,
						acceptedWarnHint: this.state.acceptedWarnHint,
						tempAcceptedWarnHint: this.state.acceptedWarnHint,
					},
				},
			]);
		}

		if (typeof onNavigationClick === 'function') {
			type = type === 'completed' ? 'next' : type;
			onNavigationClick.apply(this, [routes.anamnesis.selectCoach]);
		}
	};

	public onChangeGender = (gender: GenderKeyType) => {
		if (gender) {
			this.setState({
				selectedGender: gender,
				acceptedWarnHint: void 0,
				isPregnant: void 0,
			});
		}
	};

	public onChangeAcceptHint = (checked: boolean) => {
		this.setState({
			acceptedWarnHint: checked,
		});
	};

	public onChangePregnantState = (state: string) => {
		this.setState({
			isPregnant: !!(state === 'pregnant'),
			acceptedWarnHint: void 0,
		});
	};

	public renderButtons() {
		const ActiveButtonBar = (
			<div className="o-anamnesis__button-bar">
				<ButtonBar
					{...this.props.actionButtonsActive}
					loading={this.props.isUpdatingAnamnesis}
					handleButtonClicks={this.onHandleButtonClicks}
				/>
			</div>
		);

		if (this.state.selectedGender === 'F' && this.state.isPregnant && this.state.acceptedWarnHint) {
			return ActiveButtonBar;
		} else if (this.state.selectedGender === 'F' && this.state.isPregnant === false) {
			return ActiveButtonBar;
		} else if (this.state.selectedGender === 'M') {
			return ActiveButtonBar;
		}

		return (
			<div className="o-anamnesis__button-bar">
				<ButtonBar
					{...this.props.actionButtonsInactive}
					loading={this.props.isUpdatingAnamnesis}
					handleButtonClicks={this.onHandleButtonClicks}
				/>
			</div>
		);
	}

	public render() {
		const { genders } = this.props;

		return (
			<div>
				{Array.isArray(genders) && genders.length > 0 && (
					<GenderSelector
						selectedGender={this.state.selectedGender}
						genders={genders}
						onGenderChange={this.onChangeGender}
					/>
				)}
				<Spacer size="m" />
				{this.state.selectedGender === 'F' && (
					<div>
						<Agreement
							text={i18n('react.impulsCoach.components.anamnesis.selectGender.validation.pregnancyTitle')}
							state={this.state.isPregnant ? agreementUiStateClasses.expanded : ''}
						>
							<ElementList>
								<Radio
									checked={this.state.isPregnant === true}
									tag="li"
									name="anamnesis-pregnant"
									id="anamnesis-pregnant"
									value="pregnant"
									label={i18n(
										'react.impulsCoach.components.anamnesis.selectGender.validation.isPregnant'
									)}
									onChange={this.onChangePregnantState}
								/>
								<Radio
									checked={this.state.isPregnant === false}
									tag="li"
									name="anamnesis-pregnant"
									id="anamnesis-deny"
									value={'deny'}
									label={i18n(
										'react.impulsCoach.components.anamnesis.selectGender.validation.isNotPregnant'
									)}
									onChange={this.onChangePregnantState}
								/>
							</ElementList>
						</Agreement>
						{this.state.isPregnant && (
							<Agreement
								title={i18n(
									'react.impulsCoach.components.anamnesis.selectGender.validation.congratsTitle'
								)}
								text={i18n(
									'react.impulsCoach.components.anamnesis.selectGender.validation.congratsText'
								)}
							>
								<ElementList>
									<Checkbox
										tag="li"
										isChecked={!!this.state.acceptedWarnHint}
										id="anamnesis-pregnant-accept"
										name="anamnesis-pregnant-accept"
										label={i18n(
											'react.impulsCoach.components.anamnesis.selectGender.validation.congratsCheckboxLabel'
										)}
										onChange={(event) => this.onChangeAcceptHint(event)}
										modifier="white"
									/>
								</ElementList>
							</Agreement>
						)}
					</div>
				)}
				{this.renderButtons()}
			</div>
		);
	}
}
